import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import AppShell from "@/components/layout/AppShell";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import SettingsPage from "@/pages/SettingsPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import NewExperiencesPage from "@/pages/NewExperiencesPage";
import PractitionerApprovalsPage from "@/pages/PractitionerApprovalsPage";
import PrivacyNoticePage from "@/pages/PrivacyNoticePage";
import AudioLibraryPage from "@/pages/AudioLibraryPage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import PortalArticlesPage from "@/pages/PortalArticlesPage";
import RemoteSessionOffersPage from "@/pages/RemoteSessionOffersPage";
import ProgramsManagementPage from "@/pages/ProgramsManagementPage";
import MatrixOperationsPage from "@/pages/MatrixOperationsPage";
import ProgramRequestsPage from "@/pages/ProgramRequestsPage";
import ConsultingProgramsPage from "@/pages/ConsultingProgramsPage";
import ConsultingProgramDetailPage from "@/pages/ConsultingProgramDetailPage";
import ProgramRequestPage from "@/pages/ProgramRequestPage";
import PublicNotFoundPage from "@/pages/PublicNotFoundPage";
import SpecialInvitationPage from "@/pages/SpecialInvitationPage";
import StoneSetsPage from "@/pages/StoneSetsPage";
import PortalActivityTracker from "@/components/PortalActivityTracker";
import PortalAnalyticsPage from "@/pages/PortalAnalyticsPage";
import AdminClientPreviewPage from "@/pages/AdminClientPreviewPage";

function Protected({ children, role }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === "checking") {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-pleiades-ivory">
        <div className="text-sm text-pleiades-slate">Yükleniyor…</div>
      </div>
    );
  }
  if (status !== "authed") return <Navigate to="/giris" replace state={{ from: `${location.pathname}${location.search}` }} />;
  if (role === "staff" && user?.role === "danisan") return <Navigate to="/danisan-paneli" replace />;
  if (role === "client" && user?.role !== "danisan") return <Navigate to="/yonetim" replace />;
  return children;
}

function AnonOnly({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const requested = location.state?.from;
  const safe = typeof requested === "string" && requested.startsWith("/") && !requested.startsWith("//") ? requested : null;
  if (status === "authed") return <Navigate to={safe || (user?.role === "danisan" ? "/danisan-paneli" : "/yonetim")} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <Navigate to="/yonetim" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ConsultingProgramsPage />} />
      <Route path="/danismanlik" element={<ConsultingProgramsPage />} />
      <Route path="/danismanlik/:programSlug" element={<ConsultingProgramDetailPage />} />
      <Route
        path="/danismanlik/talep"
        element={<Protected role="client"><ProgramRequestPage /></Protected>}
      />
      <Route
        path="/kvkk-aydinlatma-metni"
        element={<PrivacyNoticePage />}
      />
      <Route
        path="/giris"
        element={
          <AnonOnly>
            <LoginPage />
          </AnonOnly>
        }
      />
      <Route
        path="/ozel-davet/:token"
        element={<Protected role="client"><SpecialInvitationPage /></Protected>}
      />
      <Route
        path="/danisan-paneli"
        element={<Protected role="client"><ClientPortalPage /></Protected>}
      />
      <Route
        path="/yonetim/danisan-onizleme/:token"
        element={<Protected role="staff"><AdminRoute><AdminClientPreviewPage /></AdminRoute></Protected>}
      />
      <Route path="/admin" element={<Navigate to="/yonetim" replace />} />
      <Route
        path="/yonetim"
        element={
          <Protected role="staff">
            <AppShell />
          </Protected>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="danisanlar" element={<ClientsPage />} />
        <Route path="danisanlar/:id" element={<ClientDetailPage />} />
        <Route path="yeni-deneyimler" element={<NewExperiencesPage />} />
        <Route path="uygulayici-onaylari" element={<PractitionerApprovalsPage />} />
        <Route path="ses-kutuphanesi" element={<AdminRoute><AudioLibraryPage /></AdminRoute>} />
        <Route path="video-kutuphanesi" element={<AdminRoute><VideoLibraryPage /></AdminRoute>} />
        <Route path="konular" element={<AdminRoute><PortalArticlesPage /></AdminRoute>} />
        <Route path="uzaktan-seanslar" element={<AdminRoute><RemoteSessionOffersPage /></AdminRoute>} />
        <Route path="dogal-tas-setleri" element={<AdminRoute><StoneSetsPage /></AdminRoute>} />
        <Route path="pdf-kutuphanesi" element={<Navigate to="/" replace />} />
        <Route path="program-yonetimi" element={<ProgramsManagementPage />} />
        <Route path="program-talepleri" element={<AdminRoute><ProgramRequestsPage /></AdminRoute>} />
        <Route path="operasyon-kuyrugu" element={<AdminRoute><MatrixOperationsPage /></AdminRoute>} />
        <Route path="istatistikler" element={<AdminRoute><PortalAnalyticsPage /></AdminRoute>} />
        <Route path="basvurular" element={<Navigate to="/" replace />} />
        <Route path="gorusmeler" element={<Navigate to="/" replace />} />
        <Route path="randevular" element={<Navigate to="/" replace />} />
        <Route path="calismalar" element={<Navigate to="/" replace />} />
        <Route path="gorevler" element={<Navigate to="/" replace />} />
        <Route path="formlar" element={<Navigate to="/" replace />} />
        <Route path="mesajlar" element={<Navigate to="/" replace />} />
        <Route path="raporlar" element={<Navigate to="/" replace />} />
        <Route path="ozel-danismanlik-etkilesimleri" element={<Navigate to="/" replace />} />
        <Route path="ayarlar" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      </Route>
      <Route path="/danisanlar" element={<Protected role="staff"><Navigate to="/yonetim/danisanlar" replace /></Protected>} />
      <Route path="/danisanlar/:id" element={<Protected role="staff"><LegacyClientRedirect /></Protected>} />
      <Route path="/yeni-deneyimler" element={<Protected role="staff"><Navigate to="/yonetim/yeni-deneyimler" replace /></Protected>} />
      <Route path="/uygulayici-onaylari" element={<Protected role="staff"><Navigate to="/yonetim/uygulayici-onaylari" replace /></Protected>} />
      <Route path="/ses-kutuphanesi" element={<Protected role="staff"><Navigate to="/yonetim/ses-kutuphanesi" replace /></Protected>} />
      <Route path="/video-kutuphanesi" element={<Protected role="staff"><Navigate to="/yonetim/video-kutuphanesi" replace /></Protected>} />
      <Route path="/program-yonetimi" element={<Protected role="staff"><Navigate to="/yonetim/program-yonetimi" replace /></Protected>} />
      <Route path="/program-talepleri" element={<Protected role="staff"><Navigate to="/yonetim/program-talepleri" replace /></Protected>} />
      <Route path="/operasyon-kuyrugu" element={<Protected role="staff"><Navigate to="/yonetim/operasyon-kuyrugu" replace /></Protected>} />
      <Route path="/ayarlar" element={<Protected role="staff"><Navigate to="/yonetim/ayarlar" replace /></Protected>} />
      <Route path="/bulunamadi" element={<PublicNotFoundPage />} />
      <Route path="*" element={<PublicNotFoundPage />} />
    </Routes>
  );
}

function LegacyClientRedirect() {
  const location = useLocation();
  return <Navigate to={`/yonetim${location.pathname}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PortalActivityTracker />
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
